import React, { useState } from "react";
import { Alert, Box, Button, Card, CardContent, LinearProgress, Paper, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Typography } from "@mui/material";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useGetEdicaoAtualQuery } from "shared/features/api/edicao/edicaoSlice";
import { DashboardProvaTecnica } from "components/DashboardProvaTecnica";
import {
  useGetDashboardInscricoesPorEstadoQuery,
  useGetDashboardInscricoesPorGeneroQuery,
  useGetDashboardInscricoesPorTipoGraduacaoQuery,
  useGetDashboardTopCidadesQuery,
  useGetDashboardInscricoesPorIdadeQuery,
  useGetDashboardInscricoesPorDiaQuery,
  useGetDashboardInscricoesPorSemestreQuery,
  useGetDashboardResumoQuery,
} from "shared/features/api/dashboard/dashboardSlice";

const colors = {
  brand: "#1557B0", brandDark: "#123B6D", accent: "#3478D4",
  accentLight: "#78A9E6", surface: "#EAF2FC", line: "#DCE3EA",
  ink: "#172B4D", muted: "#5F6F82",
};

const formatNumber = (value: number) => value.toLocaleString("pt-BR");
const formatLabel = (value: string) => {
  const normalized = value.toLocaleLowerCase("pt-BR");
  return normalized.charAt(0).toLocaleUpperCase("pt-BR") + normalized.slice(1);
};

interface ChartCardProps { title: string; children: React.ReactNode; height?: number; }
const ChartCard = ({ title, children, height = 360 }: ChartCardProps) => (
  <Card variant="outlined" sx={{ borderColor: colors.line, borderRadius: 3, height: "100%" }}>
    <CardContent>
      <Typography variant="h6" sx={{ color: colors.brandDark, fontWeight: 700, mb: 2 }}>{title}</Typography>
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </CardContent>
  </Card>
);

interface SummaryTableProps {
  title: string; firstColumn: string;
  rows: { nome: string; quantidade: number; percentual?: number }[];
  total: number; showPercentage?: boolean;
}
const SummaryTable = ({ title, firstColumn, rows, total, showPercentage = false }: SummaryTableProps) => (
  <Box>
    <Typography variant="subtitle2" sx={{ color: colors.brandDark, fontWeight: 700, mb: 1 }}>{title}</Typography>
    <TableContainer component={Paper} variant="outlined" sx={{ borderColor: colors.line }}>
      <Table size="small">
        <TableHead><TableRow sx={{ bgcolor: colors.surface }}>
          <TableCell sx={{ color: colors.muted, fontWeight: 700 }}>{firstColumn}</TableCell>
          <TableCell align="right" sx={{ color: colors.muted, fontWeight: 700 }}>{showPercentage ? "Inscrições (%)" : "Quantidade"}</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {rows.map((row) => <TableRow key={row.nome}>
            <TableCell>{row.nome}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              {formatNumber(row.quantidade)}{showPercentage && ` (${row.percentual?.toLocaleString("pt-BR") ?? 0}%)`}
            </TableCell>
          </TableRow>)}
          <TableRow><TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(total)}{showPercentage && " (100%)"}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

export const Dashboard: React.FC = () => {
  const dashboardPollingMs = 5 * 60 * 1000;
  const [currentTab, setCurrentTab] = useState(0);

  const { data: edicaoAtual, isLoading: isLoadingEdicao, isError: isErrorEdicao } = useGetEdicaoAtualQuery(undefined, {
    pollingInterval: dashboardPollingMs,
  });
  const idEdicao = edicaoAtual?.idEdicao ?? 0;
  const queryOptions = { skip: !idEdicao, pollingInterval: dashboardPollingMs };
  const { data: resumo, isLoading: isLoadingResumo, isError: isErrorResumo } = useGetDashboardResumoQuery(idEdicao, queryOptions);
  const { data: estadosResponse, isLoading: isLoadingEstados, isError: isErrorEstados } = useGetDashboardInscricoesPorEstadoQuery(idEdicao, queryOptions);
  const { data: generosResponse, isLoading: isLoadingGeneros, isError: isErrorGeneros } = useGetDashboardInscricoesPorGeneroQuery(idEdicao, queryOptions);
  const { data: graduacoesResponse, isLoading: isLoadingGraduacoes, isError: isErrorGraduacoes } = useGetDashboardInscricoesPorTipoGraduacaoQuery(idEdicao, queryOptions);
  const { data: cidadesResponse, isLoading: isLoadingCidades, isError: isErrorCidades } = useGetDashboardTopCidadesQuery(idEdicao, queryOptions);
  const { data: idadesResponse, isLoading: isLoadingIdades, isError: isErrorIdades } = useGetDashboardInscricoesPorIdadeQuery(idEdicao, queryOptions);
  const { data: diasResponse, isLoading: isLoadingDias, isError: isErrorDias } = useGetDashboardInscricoesPorDiaQuery(idEdicao, queryOptions);
  const { data: semestresResponse, isLoading: isLoadingSemestres, isError: isErrorSemestres } = useGetDashboardInscricoesPorSemestreQuery(idEdicao, queryOptions);

  const estados = (estadosResponse ?? [])
    .filter((item) => item.estado.toLocaleLowerCase("pt-BR") !== "total")
    .map((item) => ({ nome: item.estado, quantidade: item.quantidade }));
  const generos = (generosResponse ?? []).map((item) => ({
    nome: formatLabel(item.genero), quantidade: item.quantidade, percentual: item.percentual,
  }));
  const totalGraduacaoResponse = (graduacoesResponse ?? []).find(
    (item) => item.tipoGraduacao.toLocaleLowerCase("pt-BR") === "total"
  );
  const graduacoesSemTotal = (graduacoesResponse ?? [])
    .filter((item) => item.tipoGraduacao.toLocaleLowerCase("pt-BR") !== "total");
  const totalGraduacao = totalGraduacaoResponse?.quantidade
    ?? graduacoesSemTotal.reduce((total, item) => total + item.quantidade, 0);
  const tiposGraduacao = graduacoesSemTotal.map((item) => ({
    nome: formatLabel(item.tipoGraduacao),
    quantidade: item.quantidade,
    percentual: totalGraduacao > 0 ? Number(((item.quantidade / totalGraduacao) * 100).toFixed(1)) : 0,
  }));
  const cidades = (cidadesResponse ?? []).map((item) => ({
    nome: `${item.estado} - ${formatLabel(item.cidade)}`,
    quantidade: item.quantidade,
  }));
  const idades = idadesResponse ?? [];
  const inscricoesPorDia = (diasResponse ?? []).map((item) => ({
    ...item,
    data: new Date(`${item.data}T00:00:00`).toLocaleDateString("pt-BR"),
  }));
  const semestres = (semestresResponse ?? [])
    .map((item) => {
      const semestreLabel = String(item.semestre);
      const semestreNumero = parseInt(semestreLabel.match(/\d+/)?.[0] ?? "", 10);

      return {
        semestre: semestreLabel,
        semestreNumero: Number.isNaN(semestreNumero) ? Number.MAX_SAFE_INTEGER : semestreNumero,
        quantidade: item.quantidade,
      };
    })
    .sort((a, b) => a.semestreNumero - b.semestreNumero);
  const totalGeneros = generos.reduce((total, item) => total + item.quantidade, 0);
  const isLoading = isLoadingEdicao || isLoadingResumo || isLoadingEstados || isLoadingGeneros
    || isLoadingGraduacoes || isLoadingCidades || isLoadingIdades || isLoadingDias
    || isLoadingSemestres;
  const hasError = isErrorEdicao || isErrorResumo || isErrorEstados || isErrorGeneros
    || isErrorGraduacoes || isErrorCidades || isErrorIdades || isErrorDias
    || isErrorSemestres;
  const maiorEstado = Math.max(...estados.map((item) => Number(item.quantidade)));
  const maiorSemestre = Math.max(...semestres.map((item) => item.quantidade));
  const tooltipFormatter = (value: number) => [formatNumber(value), "Quantidade"];

  return <Box sx={{ bgcolor: "#F4F7FA", mx: { xs: -3, sm: 0 }, p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
    <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
      <Tabs
        value={currentTab}
        onChange={(_, newValue) => setCurrentTab(newValue)}
        aria-label="Abas do dashboard"
      >
        <Tab label="Inscrições" value={0} />
        <Tab label="Prova técnica" value={3} />
      </Tabs>
    </Box>

    {currentTab === 0 && <>
    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
      <Button
        variant="outlined"
        onClick={() => window.location.reload()}
        disabled={isLoading}
      >
        Atualizar pagina
      </Button>
    </Box>
    {isLoading && <LinearProgress sx={{ mb: 2 }} />}
    {hasError && <Alert severity="error" sx={{ mb: 2 }}>Não foi possível carregar todos os dados do dashboard.</Alert>}
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2fr) minmax(300px, 1fr)" }, gap: 2 }}>
      <ChartCard title="Top 15 Cidades com mais Inscrições" height={520}><ResponsiveContainer>
        <BarChart data={cidades} layout="vertical" margin={{ top: 8, right: 35, left: 45, bottom: 0 }}>
          <CartesianGrid stroke={colors.line} horizontal={false} /><XAxis type="number" allowDecimals={false} tick={{ fill: colors.muted }} />
          <YAxis type="category" dataKey="nome" width={150} tick={{ fill: colors.muted, fontSize: 11 }} /><Tooltip formatter={tooltipFormatter} />
          <Bar dataKey="quantidade" fill={colors.accent} radius={[0, 6, 6, 0]}><LabelList dataKey="quantidade" position="right" fill={colors.brandDark} fontSize={11} /></Bar>
        </BarChart>
      </ResponsiveContainer></ChartCard>

      <Card variant="outlined" sx={{ borderColor: colors.line, borderRadius: 3 }}><CardContent>
        <Typography variant="h6" sx={{ color: colors.brandDark, fontWeight: 700, mb: 2 }}>Resumo</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.5, mb: 2 }}>
          {[{ label: "TOTAL DE INSCRIÇÕES", value: resumo?.totalInscricoes ?? 0 }, { label: "ESTADOS", value: resumo?.totalEstados ?? 0 }].map((item) =>
            <Paper key={item.label} variant="outlined" sx={{ p: 1.5, bgcolor: colors.surface, borderColor: colors.line, borderLeft: `4px solid ${colors.accent}` }}>
              <Typography variant="caption" color={colors.muted}>{item.label}</Typography><Typography variant="h5" fontWeight={700} color={colors.ink}>{item.value}</Typography>
            </Paper>)}
        </Box>
        <Stack spacing={2}><SummaryTable title="Inscrições por gênero" firstColumn="Gênero" rows={generos} total={totalGeneros} showPercentage />
          <SummaryTable title="Inscrições por Tipo de Graduação" firstColumn="Tipo de graduação" rows={tiposGraduacao} total={totalGraduacao} showPercentage />
          {resumo?.ultimaAtualizacao && <Typography variant="caption" color={colors.muted}>
            Última atualização: {new Date(resumo.ultimaAtualizacao).toLocaleString("pt-BR")}
          </Typography>}
        </Stack>
      </CardContent></Card>
    </Box>

    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
        gap: 2,
        mt: 2,
      }}
    >
      <ChartCard title="Inscrições por Estado" height={420}><ResponsiveContainer>
        <BarChart data={estados} margin={{ top: 24, right: 12, left: -10, bottom: 0 }}>
          <CartesianGrid stroke={colors.line} vertical={false} /><XAxis dataKey="nome" tick={{ fill: colors.muted, fontSize: 12 }} />
          <YAxis allowDecimals={false} tick={{ fill: colors.muted, fontSize: 12 }} /><Tooltip formatter={tooltipFormatter} />
          <Bar dataKey="quantidade" radius={[6, 6, 0, 0]}>{estados.map((item) => <Cell key={String(item.nome)} fill={Number(item.quantidade) === maiorEstado ? colors.brandDark : colors.accent} />)}
            <LabelList dataKey="quantidade" position="top" fill={colors.brandDark} fontSize={10} /></Bar>
        </BarChart>
      </ResponsiveContainer></ChartCard>

      <ChartCard title="Inscrições por Idade" height={380}><ResponsiveContainer>
        <BarChart data={idades} margin={{ top: 24, right: 12, left: -10, bottom: 0 }}>
          <CartesianGrid stroke={colors.line} vertical={false} /><XAxis dataKey="idade" tick={{ fill: colors.muted, fontSize: 11 }} />
          <YAxis allowDecimals={false} tick={{ fill: colors.muted }} /><Tooltip formatter={tooltipFormatter} />
          <Bar dataKey="quantidade" fill={colors.accentLight} radius={[6, 6, 0, 0]}><LabelList dataKey="quantidade" position="top" fill={colors.brandDark} fontSize={10} /></Bar>
        </BarChart>
      </ResponsiveContainer></ChartCard>

      <ChartCard title="Inscrições por Dia" height={320}><ResponsiveContainer>
        <AreaChart data={inscricoesPorDia} margin={{ top: 24, right: 20, left: -5, bottom: 0 }}>
          <defs><linearGradient id="dashboardArea" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={colors.accent} stopOpacity={0.3} /><stop offset="95%" stopColor={colors.accent} stopOpacity={0.03} /></linearGradient></defs>
          <CartesianGrid stroke={colors.line} vertical={false} /><XAxis dataKey="data" tick={{ fill: colors.muted }} /><YAxis allowDecimals={false} tick={{ fill: colors.muted }} />
          <Tooltip formatter={tooltipFormatter} /><Area type="monotone" dataKey="quantidade" stroke={colors.brand} strokeWidth={3} fill="url(#dashboardArea)" />
        </AreaChart>
      </ResponsiveContainer></ChartCard>

      <ChartCard title="Inscrições por Semestre" height={360}><ResponsiveContainer>
        <BarChart data={semestres} layout="vertical" margin={{ top: 8, right: 35, left: 0, bottom: 0 }}>
          <CartesianGrid stroke={colors.line} horizontal={false} /><XAxis type="number" allowDecimals={false} tick={{ fill: colors.muted }} />
          <YAxis type="category" dataKey="semestre" tick={{ fill: colors.muted }} /><Tooltip formatter={tooltipFormatter} />
          <Bar dataKey="quantidade" radius={[0, 6, 6, 0]}>{semestres.map((item) => <Cell key={item.semestre} fill={item.quantidade === maiorSemestre ? colors.brandDark : colors.accent} />)}
            <LabelList dataKey="quantidade" position="right" fill={colors.brandDark} fontSize={11} /></Bar>
        </BarChart>
      </ResponsiveContainer></ChartCard>
    </Box>
    </>}

    {currentTab === 3 && <DashboardProvaTecnica />}
  </Box>;
};
